import { LogEntryPhoto } from "../models/logs/photos/LogEntryPhoto.interface";
import { LogEntryPhotoFile } from "../models/logs/photos/LogEntryPhotoFiles.interface";

/**
 * Copies photos to clipboard using document selection
 * @param photos Array of LogEntryPhoto objects to copy
 * @returns Promise that resolves when operation completes
 */
export async function copyPhotosToClipboard(photos: LogEntryPhoto[], successMessage: string, isMobile: boolean = false): Promise<void> {
  if (photos.length === 0) {
    return;
  }

  if (isMobile) {
    displayToastNotification('Unable to copy photos to clipboard. Your browser may not support this feature.', 'error');
    return;
  }

  try {
    // Create a temporary div to hold the content
    const container = document.createElement('div');
    container.contentEditable = 'true'; // Make it editable to ensure copy works
    container.style.position = 'fixed';
    container.style.left = '-9999px'; // Position off-screen
    container.style.top = '0';
    document.body.appendChild(container);
    
    // Load all images into the container
    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      
      // Add a paragraph for the label
      const label = document.createElement('p');
      label.textContent = `Photo ${i + 1}: ${photo.name || 'Attachment'}`;
      container.appendChild(label);
      
      // Add the image
      const imgHtml = `<img src="${photo.url}" style="max-width: 100%; height: auto; object-fit: contain;" alt="Photo ${i + 1}">`;
      container.innerHTML += imgHtml;
      
      // Add some spacing
      container.innerHTML += '<br><br>';
    }

    // Wait a moment for images to render
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Focus the container and select all content
    container.focus();
    
    // Use the selection API
    const range = document.createRange();
    range.selectNodeContents(container);
    
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Copy the selected content
    const successful = document.execCommand('copy');

    // Clean up
    if (selection) {
      selection.removeAllRanges();
    }
    
    document.body.removeChild(container);
    
    // Notification based on result
    if (successful) {
      displayToastNotification(successMessage, 'success');
    } else {
      displayToastNotification('Unable to copy photos to clipboard. Your browser may not support this feature.', 'error');
    }
    
  } catch (error) {
    console.error('Error copying photos:', error);
    displayToastNotification('Unable to copy photos to clipboard. Your browser may not support this feature.', 'error');
  }
}

// Keep your existing displayToastNotification function
function displayToastNotification(message: string, type: 'success' | 'error' | 'warning'): void {
  // Replace with your actual toast notification implementation
  // For example, if using ionic/angular:
  // this.toastService.present(message, type);
  
  // Simple fallback alert if no toast service is available
  if (type === 'error') {
    alert(message);
  } else {
    alert(message);
  }
}

// Process photos for clipboard
export function processPhotosForClipboard(files: LogEntryPhotoFile[]): void {
  const imageFiles = files.filter(f => f.contentType.startsWith('image/'));
  
  if (imageFiles.length > 0) {
    // Convert LogEntryPhotoFile[] to LogEntryPhoto[]
    const logEntryPhotos: LogEntryPhoto[] = imageFiles.map(file => ({
      id: file.id.toString(), // Convert number to string
      url: `data:${file.contentType};base64,${file.fileContent}`,
      name: file.fileName || 'Photo'
    }));

    // Use the copyPhotosToClipboard function
    copyPhotosToClipboard(
      logEntryPhotos,
      'Photos copied to clipboard. You can paste them into your email.',
      false
    );
  } else {
    displayToastNotification('No image files found to copy to clipboard', 'error');
  }
}